import React, { ReactNode, useState } from 'react';
import { Box, Typography, Link, AppBar, Toolbar, IconButton } from '@mui/material';
import { Menu as MenuIcon, AutoAwesome } from '@mui/icons-material';
import Sidebar from './Sidebar';
import { useNavigate } from 'react-router-dom';

interface MainLayoutProps {
  children: ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Mobile AppBar */}
      <AppBar
        position="fixed"
        sx={{
          display: { xs: 'block', md: 'none' },
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderBottomColor: 'divider',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, color: 'text.primary' }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesome sx={{ color: 'primary.main', fontSize: 24 }} />
            <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700 }}>
              TextWiz
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Sidebar mobileOpen={mobileOpen} onMobileClose={handleDrawerToggle} />
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          backgroundColor: 'background.default',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100%', md: 'calc(100% - 260px)' },
          mt: { xs: '64px', md: 0 }, // Add top margin on mobile for AppBar
        }}
      >
        <Box sx={{ flexGrow: 1, mb: 4 }}>
          {children}
        </Box>
        
        {/* Footer */}
        <Box
          component="footer"
          sx={{
            mt: 'auto',
            py: 3,
            px: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              maxWidth: 1200,
              mx: 'auto',
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: { xs: 'center', sm: 'left' } }}>
              © {new Date().getFullYear()} TextWiz. Made for Aphantasia.
            </Typography>
            
            <Box
              sx={{
                display: 'flex',
                gap: 3,
                flexWrap: 'wrap',
                justifyContent: 'center',
              }}
            >
              <Link
                href="mailto:support@text-wiz.com"
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  '&:hover': {
                    color: 'primary.light',
                    textDecoration: 'underline',
                  },
                }}
              >
                support@text-wiz.com
              </Link>
              <Link
                component="button"
                onClick={() => navigate('/terms')}
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'primary.light',
                    textDecoration: 'underline',
                  },
                }}
              >
                Terms of Service
              </Link>
              <Link
                component="button"
                onClick={() => navigate('/privacy')}
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'primary.light',
                    textDecoration: 'underline',
                  },
                }}
              >
                Privacy Policy
              </Link>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MainLayout; 